import React from 'react';
import { Lock, ShieldCheck, ArrowRight, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

// Payment links per module and bundle
const STRIPE_LINKS = {
  'module-2': 'https://buy.stripe.com/cNi7sK71n0eDg3HahJ8so05',
  'module-3': 'https://buy.stripe.com/fZueVcadz1iH7xb9dF8so03',
  'module-4': 'https://buy.stripe.com/dRm8wO5Xje5tdVz0H98so04',
  'bundle':   'https://buy.stripe.com/cNieVc85r7H5dVz3Tl8so07',
};

const CoursePaywall = ({ isAuthenticated = false, moduleNumber = 2, moduleName = null }) => {
  const displayName = moduleName || `Module ${moduleNumber}`;
  const moduleKey = `module-${moduleNumber}`;
  const moduleLink = STRIPE_LINKS[moduleKey] || STRIPE_LINKS['module-2'];

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-10 text-center">
          <div className="w-16 h-16 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="text-navy" size={32} />
          </div>
          <h2 className="font-serif text-2xl font-bold text-navy mb-3">Sign In to Continue</h2>
          <p className="text-slate-600 mb-8">Please log in to access {displayName}.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/signin?redirect=${encodeURIComponent(window.location.pathname)}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-navy text-white font-semibold rounded-lg hover:bg-navy/90 transition-colors shadow-md"
              data-testid="paywall-signin-btn">
              Sign In <ArrowRight size={18} />
            </Link>
            <Link to={`/signup?redirect=${encodeURIComponent(window.location.pathname)}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-200 text-navy font-semibold rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="paywall-signup-btn">
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full space-y-4">

        {/* Individual module purchase */}
        <div className="bg-[#071B3A] rounded-2xl shadow-2xl p-8 text-white text-center">
          <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-5 border border-gold/30">
            <Lock className="text-gold" size={28} />
          </div>
          <h2 className="font-serif text-2xl font-light mb-2">Unlock {displayName}</h2>
          <p className="text-white/50 text-sm mb-6">One module. Lifetime access. Learn at your own pace.</p>

          <div className="bg-white/5 rounded-xl p-5 mb-6 border border-white/10">
            <p className="font-serif text-4xl text-gold font-light">£10</p>
            <p className="text-white/30 text-xs mt-1 tracking-wide">per module</p>
          </div>

          <a href={moduleLink} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 bg-gold text-navy font-bold rounded-xl hover:bg-gold/90 transition-all shadow-lg"
            data-testid="paywall-unlock-btn">
            <Lock size={18} /> Unlock This Module — £10
          </a>
          <p className="flex items-center justify-center gap-1.5 text-xs text-white/30 mt-3">
            <ShieldCheck size={12} className="text-green-400" /> Secure checkout · Stripe
          </p>
        </div>

        {/* Bundle offer */}
        <div className="border border-gold/20 rounded-2xl p-6 text-center bg-white/[0.02]">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Package size={14} className="text-gold/60" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold/60">Best Value</p>
          </div>
          <p className="text-cream font-serif text-lg font-light mb-1">All Three Paid Modules</p>
          <p className="text-cream/30 text-xs mb-4">Modules 2, 3 &amp; 4 · Save £10 vs buying separately</p>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-cream/25 text-base line-through">£30</span>
            <span className="font-serif text-3xl text-gold font-light">£20</span>
          </div>
          <a href={STRIPE_LINKS['bundle']} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 border border-gold/30 text-gold text-sm font-medium rounded-xl hover:bg-gold hover:text-navy transition-all"
            data-testid="paywall-bundle-btn">
            Get Bundle — £20 <ArrowRight size={14} />
          </a>
        </div>

        <div className="text-center">
          <Link to="/courses" className="text-slate-400 hover:text-slate-600 text-sm underline underline-offset-4">
            ← Back to Course Overview
          </Link>
        </div>

      </div>
    </div>
  );
};

export default CoursePaywall;
